import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { movies as allMovies, movieGenres } from '@/mocks/movies';
import { actors } from '@/mocks/actors';

const PAGE_SIZE = 8;

export default function Movies() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelOpen, setPanelOpen] = useState(false);

  const keyword = searchParams.get('q') || '';
  const genre = searchParams.get('genre') || '全部';
  const year = searchParams.get('year') || '全部';
  const sort = searchParams.get('sort') || 'latest';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [draftKeyword, setDraftKeyword] = useState(keyword);
  useEffect(() => {
    setDraftKeyword(keyword);
  }, [keyword]);

  const actorNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    actors.forEach((a) => {
      map[a.id] = a.name;
    });
    return map;
  }, []);

  const years = useMemo(
    () => ['全部', ...Array.from(new Set(allMovies.map((m) => m.year))).sort((a, b) => b - a).map(String)],
    [],
  );

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    let list = allMovies.filter((m) => {
      const matchGenre = genre === '全部' || m.genres.includes(genre);
      const matchYear = year === '全部' || String(m.year) === year;
      const matchKeyword =
        !k ||
        m.title.toLowerCase().includes(k) ||
        m.actorIds.some((id) => (actorNameMap[id] || '').toLowerCase().includes(k));
      return matchGenre && matchYear && matchKeyword;
    });

    if (sort === 'latest') {
      list = [...list].sort((a, b) => b.year - a.year);
    } else if (sort === 'hot') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sort === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [keyword, genre, year, sort, actorNameMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParams = (next: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (!value || value === '全部' || (key === 'sort' && value === 'latest') || (key === 'page' && value === '1')) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    setSearchParams(params);
    setPanelOpen(false);
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
    setDraftKeyword('');
    setPanelOpen(false);
  };

  const filterControls = (
    <>
      <div>
        <label className="mb-1.5 block text-xs font-medium text-foreground-500">影片类型</label>
        <select
          value={genre}
          onChange={(e) => updateParams({ genre: e.target.value, page: undefined })}
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
        >
          {movieGenres.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium text-foreground-500">上映年份</label>
        <select
          value={year}
          onChange={(e) => updateParams({ year: e.target.value, page: undefined })}
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <SiteLayout>
      <title>电影资源分类 - 蜜桃成熟时33D及同类型影片列表</title>
      <meta
        name="description"
        content="电影资源分类页汇总蜜桃成熟时33D及相关系列与同类型影视作品，支持按类型、年份筛选和关键词搜索，方便快速查找目标影片。"
      />
      <meta name="keywords" content="电影分类,蜜桃成熟时33D系列,同类型影片,影视作品列表,影片筛选" />
      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影分类' }]} />

        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            电影资源分类
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            本页汇总《蜜桃成熟时33D》及其相关系列与同类型影视作品，支持按类型、年份筛选与关键词搜索，帮助你快速找到想看的影片。
          </p>
        </div>

        {/* Toolbar */}
        <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-end md:gap-6 md:p-5">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ q: draftKeyword.trim() || undefined, page: undefined });
            }}
            className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2"
          >
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={draftKeyword}
              onChange={(e) => setDraftKeyword(e.target.value)}
              type="text"
              name="q"
              placeholder="输入电影标题或演员姓名"
              className="w-full bg-transparent py-1 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="hidden items-end gap-4 md:flex">
            {filterControls}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-foreground-500">排序方式</label>
              <select
                value={sort}
                onChange={(e) => updateParams({ sort: e.target.value })}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                <option value="latest">最新上映</option>
                <option value="hot">最多浏览</option>
                <option value="rating">评分最高</option>
              </select>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-800 md:hidden cursor-pointer"
          >
            <i className="ri-filter-3-line"></i>
            筛选 / 排序
          </button>
        </div>

        {/* Mobile filter panel */}
        {panelOpen && (
          <div className="mt-3 grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-3 md:hidden">
            {filterControls}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-foreground-500">排序方式</label>
              <select
                value={sort}
                onChange={(e) => updateParams({ sort: e.target.value })}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                <option value="latest">最新上映</option>
                <option value="hot">最多浏览</option>
                <option value="rating">评分最高</option>
              </select>
            </div>
          </div>
        )}

        {/* Result meta */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 部影片
            {keyword && <span className="ml-2 text-foreground-500">（关键词：{keyword}）</span>}
          </p>
          {(keyword || genre !== '全部' || year !== '全部') && (
            <button
              type="button"
              onClick={resetAll}
              className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          )}
        </div>

        {/* Grid */}
        {pageItems.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {pageItems.map((m) => (
              <MovieCard
                key={m.id}
                movie={m}
                actorNames={m.actorIds.slice(0, 2).map((id) => actorNameMap[id])}
              />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              没有找到匹配的影片
            </h3>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              换个关键词试试，或者重置筛选条件查看全部影片。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => updateParams({ page: String(currentPage - 1) })}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors enabled:hover:border-primary-400 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line"></i>
              上一页
            </button>
            {Array.from({ length: totalPages }).map((_, idx) => {
              const n = idx + 1;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => updateParams({ page: String(n) })}
                  className={`h-10 w-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    n === currentPage
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => updateParams({ page: String(currentPage + 1) })}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors enabled:hover:border-primary-400 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        <p className="mt-10 text-center text-xs text-foreground-400">
          本站为演示项目，所有影片资料均为虚构内容，不提供真实播放与下载服务。
        </p>
      </div>
    </SiteLayout>
  );
}